import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import {
  CompactInsightsSection,
  ConversationCustomerCard,
  ConversationActionsTab,
  ConversationDetailsActions,
  ConversationFactsStrip,
  ConversationFollowUpPill,
  ConversationGenieIdentityCard,
  ConversationImagesGallery,
  ConversationImproveGenieCTA,
  ConversationInsightFunnel,
  ConversationKnowledgeGaps,
  ConversationNextStepsCard,
  ConversationOwnerNotes,
  ConversationShareButton,
  ConversationSuggestedFollowUp,
  ConversationSummaryHero,
  ListenedCallFollowUpCard,
  ListenedEmailReplyCard,
  RecordingActionPlanCard,
  RecordingAttendeesCard,
  RecordingDecisionsCard,
  RecordingKeyTakeawaysCard,
  RecordingRecommendationsCard,
  TranscriptCopyButton,
  TranscriptTab,
} from "@/components/conversation/details";
import { Button } from "@/components/ui/button";
import { GenieLoader } from "@/components/ui/GenieLoader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useConversationDetails } from "@/hooks/conversation/useConversationDetails";
import { useConversationById } from "@/hooks/conversation/useConversations";
import { isListenedCall } from "@/lib/conversation/listenedCall";
import { isListenedEmail } from "@/lib/conversation/listenedEmail";
import { isRecordedMeeting } from "@/lib/conversation/recordedMeeting";
import { Conversation } from "@/types/conversation/conversation";

/**
 * Full-page conversation analysis at `/genie-hub/conversations/:id`.
 *
 * Transcript-first layout: hero identity → quiet facts strip (channel,
 * date, duration, quality) → transcript in the main column with a slim
 * context rail beside it (summary, customer, improve-genie, follow-up).
 * Recordings keep the tabbed overview/transcript layout.
 */
export default function ConversationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: conversation, isLoading, isError } = useConversationById(id ?? null);

  if (isLoading) {
    return <GenieLoader size="fullscreen" loadingText="Loading conversation…" />;
  }

  if (isError || !conversation) {
    return (
      <div className="min-h-screen bg-[#F9FAFC] flex items-center justify-center">
        <Helmet>
          <title>Conversation not found · Help Genie</title>
          <meta name="robots" content="noindex,nofollow" />
        </Helmet>
        <div className="text-center max-w-md px-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Conversation not found</h2>
          <p className="text-gray-600 mb-6">
            The conversation you're looking for doesn't exist or you don't have access to it.
          </p>
          <Button
            onClick={() => navigate("/genie-hub/conversations")}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Conversations
          </Button>
        </div>
      </div>
    );
  }

  return <ConversationDetailPageContent conversation={conversation} />;
}

interface ConversationDetailPageContentProps {
  conversation: Conversation;
  inDrawer?: boolean;
}

export function ConversationDetailPageContent({ conversation, inDrawer = false }: ConversationDetailPageContentProps) {
  const navigate = useNavigate();
  const state = useConversationDetails(conversation, true);
  const {
    currentConversation,
    isLoadingDetails,
    isLoadingConversationMedias,
    conversationMedias,
    transcript,
    analysisResult,
    topics,
    shouldShowLoadingForAnalysis,
    handleDeleteConversationMedia,
    handleDeleteAllConversationMedias,
  } = state;

  if (!currentConversation) return null;

  const agentName = currentConversation.agent?.name || "Conversation";
  const primaryColor = currentConversation.agent?.branding?.primaryColor || "#4E9CFF";
  const summaryLoading = isLoadingDetails || shouldShowLoadingForAnalysis;
  const isRecording = isRecordedMeeting(currentConversation.type);

  return (
    <div className={inDrawer ? "bg-[#F9FAFC]" : "min-h-screen bg-[#F9FAFC]"}>
      <Helmet>
        <title>{`Analysis – ${agentName} · Help Genie`}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div className={inDrawer ? "mx-auto py-2 max-w-[1400px]" : "container mx-auto px-4 py-6 md:py-8 max-w-[1400px]"}>
        {/* Top bar: back + title + actions */}
        <div className={`flex flex-col gap-4 md:flex-row md:items-center mb-6 ${inDrawer ? "md:justify-end" : "md:justify-between"}`}>
          {!inDrawer && (
            <div className="flex items-center gap-3 min-w-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/genie-hub/conversations")}
                className="flex items-center gap-1 text-gray-600 hover:bg-primary/10 hover:text-primary -ml-2 flex-shrink-0"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Conversations</span>
              </Button>
              <div className="hidden md:block w-px h-6 flex-shrink-0" style={{ backgroundColor: `${primaryColor}40` }} />
              <h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 truncate">Analysis</h1>
            </div>
          )}
          <div className="flex-shrink-0 flex items-center gap-2 flex-wrap">
            <TranscriptCopyButton conversation={currentConversation} transcript={transcript} />
            <ConversationFollowUpPill conversationId={currentConversation.id} />
            <ConversationShareButton conversationId={currentConversation.id} />
            <ConversationDetailsActions state={state} />
          </div>
        </div>

        {/* Genie identity strip */}
        <ConversationGenieIdentityCard conversation={currentConversation} />

        {/* Quiet facts strip — replaces the three stacked fact boxes */}
        <ConversationFactsStrip conversation={currentConversation} />

        {isRecording ? (
          /* Recordings keep the tabbed overview/transcript layout */
          <Tabs defaultValue="overview" className="w-full">
            <TabsList
              className="grid h-auto w-max grid-cols-3 gap-1 mb-6 border-2"
              style={
                {
                  "--primary-color": primaryColor,
                  backgroundColor: `${primaryColor}10`,
                  borderColor: `${primaryColor}20`,
                } as React.CSSProperties
              }
            >
              <TabsTrigger
                value="overview"
                className="text-sm px-4 whitespace-nowrap data-[state=active]:bg-[var(--primary-color)] data-[state=active]:text-white data-[state=active]:border-[var(--primary-color)]"
              >
                Overview & Analysis
              </TabsTrigger>
              <TabsTrigger
                value="transcript"
                className="text-sm px-4 whitespace-nowrap data-[state=active]:bg-[var(--primary-color)] data-[state=active]:text-white data-[state=active]:border-[var(--primary-color)]"
              >
                Transcript
              </TabsTrigger>
              <TabsTrigger
                value="actions"
                className="text-sm px-4 whitespace-nowrap data-[state=active]:bg-[var(--primary-color)] data-[state=active]:text-white data-[state=active]:border-[var(--primary-color)]"
              >
                Actions
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-0">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                  <ConversationSummaryHero
                    conversation={currentConversation}
                    topics={topics}
                    isLoading={summaryLoading}
                  />
                  <ListenedCallFollowUpCard conversation={currentConversation} />
                  <RecordingAttendeesCard aiResult={analysisResult} isLoading={summaryLoading} />
                  <RecordingDecisionsCard aiResult={analysisResult} isLoading={summaryLoading} />
                  <RecordingActionPlanCard aiResult={analysisResult} isLoading={summaryLoading} />
                  <ConversationOwnerNotes conversationId={currentConversation.id} />
                </div>
                <aside className="lg:col-span-1 space-y-5">
                  <RecordingKeyTakeawaysCard aiResult={analysisResult} isLoading={summaryLoading} />
                  <RecordingRecommendationsCard aiResult={analysisResult} isLoading={summaryLoading} />
                  <ConversationNextStepsCard conversation={currentConversation} />
                  {/* A Listener genie's phone call is followed up from ListenedCallFollowUpCard. */}
                  {!isListenedCall(currentConversation) && (
                    <ConversationSuggestedFollowUp conversation={currentConversation} />
                  )}
                </aside>
              </div>
            </TabsContent>

            <TabsContent value="transcript" className="mt-0">
              <TranscriptTab transcript={transcript} conversation={currentConversation} />
            </TabsContent>

            <TabsContent value="actions" className="mt-0">
              <ConversationActionsTab state={state} />
            </TabsContent>
          </Tabs>
        ) : (
          /* Conversations: three tabs — Insights leads (summary, captured
             insights, funnel, knowledge gaps), Transcript reads full-width
             on its own tab, Actions holds re-analysis and the email report.
             Insights is the default so the analysis is the first thing you
             see. */
          <Tabs defaultValue="insights" className="w-full">
            <TabsList
              className="grid h-auto w-max grid-cols-3 gap-1 mb-6 border-2"
              style={
                {
                  "--primary-color": primaryColor,
                  backgroundColor: `${primaryColor}10`,
                  borderColor: `${primaryColor}20`,
                } as React.CSSProperties
              }
            >
              <TabsTrigger
                value="insights"
                className="text-sm px-4 whitespace-nowrap data-[state=active]:bg-[var(--primary-color)] data-[state=active]:text-white data-[state=active]:border-[var(--primary-color)]"
              >
                Insights
              </TabsTrigger>
              <TabsTrigger
                value="transcript"
                className="text-sm px-4 whitespace-nowrap data-[state=active]:bg-[var(--primary-color)] data-[state=active]:text-white data-[state=active]:border-[var(--primary-color)]"
              >
                Transcript
              </TabsTrigger>
              <TabsTrigger
                value="actions"
                className="text-sm px-4 whitespace-nowrap data-[state=active]:bg-[var(--primary-color)] data-[state=active]:text-white data-[state=active]:border-[var(--primary-color)]"
              >
                Actions
              </TabsTrigger>
            </TabsList>

            <TabsContent value="insights" className="mt-0">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main column — the analysis, in priority order */}
                <div className="lg:col-span-2 space-y-6">
                  <ConversationSummaryHero
                    conversation={currentConversation}
                    topics={topics}
                    isLoading={summaryLoading}
                  />
                  <ListenedEmailReplyCard conversation={currentConversation} />
                  <CompactInsightsSection conversation={currentConversation} />
                  <ConversationInsightFunnel conversation={currentConversation} />
                  <ConversationKnowledgeGaps conversation={currentConversation} />
                  <ConversationImagesGallery
                    medias={conversationMedias}
                    isLoading={isLoadingConversationMedias}
                    onDeleteMedia={(media) => handleDeleteConversationMedia(media.id)}
                    onDeleteAll={handleDeleteAllConversationMedias}
                  />
                  <ConversationOwnerNotes conversationId={currentConversation.id} />
                </div>

                {/* Right rail — who this was, then act on this conversation */}
                <aside className="lg:col-span-1 space-y-5">
                  <ConversationCustomerCard conversation={currentConversation} />
                  <ConversationImproveGenieCTA conversation={currentConversation} />
                  <ConversationNextStepsCard conversation={currentConversation} />
                  {/* A forwarded email is answered from ListenedEmailReplyCard, through the genie's forwarding address. */}
                  {!isListenedEmail(currentConversation.type) && (
                    <ConversationSuggestedFollowUp conversation={currentConversation} />
                  )}
                </aside>
              </div>
            </TabsContent>

            <TabsContent value="transcript" className="mt-0">
              <TranscriptTab transcript={transcript} conversation={currentConversation} />
            </TabsContent>

            <TabsContent value="actions" className="mt-0">
              <ConversationActionsTab state={state} />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
